import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface CompanyInfo {
  logoUrl: string | null;
  companyName: string | null;
  userName: string | null;
}

export function useCompanyInfo() {
  const { user } = useAuth();
  const [info, setInfo] = useState<CompanyInfo>({
    logoUrl: null,
    companyName: null,
    userName: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchInfo = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Fetch profile data
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, company_name, company_logo_url")
        .eq("user_id", user.id)
        .single();

      // Check if user is part of a company
      const { data: memberData } = await supabase
        .from("company_members")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();

      let logoUrl = profileData?.company_logo_url || null;
      let companyName = profileData?.company_name || null;

      if (memberData?.company_id) {
        // Get company data (takes priority)
        const { data: companyData } = await supabase
          .from("companies")
          .select("logo_url, name")
          .eq("id", memberData.company_id)
          .single();

        if (companyData) {
          logoUrl = companyData.logo_url || logoUrl;
          companyName = companyData.name || companyName;
        }
      }

      // Build user name
      const userName = [profileData?.first_name, profileData?.last_name]
        .filter(Boolean)
        .join(" ") || null;

      setInfo({ logoUrl, companyName, userName });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInfo();

    if (!user?.id) return;

    // Subscribe to realtime changes on profiles
    const profileChannel = supabase
      .channel(`profile-changes-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchInfo()
      )
      .subscribe();

    // Subscribe to realtime changes on companies (via company_members)
    const companiesChannel = supabase
      .channel(`company-changes-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "companies",
        },
        () => fetchInfo()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profileChannel);
      supabase.removeChannel(companiesChannel);
    };
  }, [user?.id, fetchInfo]);

  return { ...info, loading, refetch: fetchInfo };
}
